import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import PermitBuilder from './PermitBuilder';
import { 
    FileSignature, Settings, Search, Download, 
    CheckCircle2, XCircle, AlertTriangle, 
    Clock, MapPin, Building2, Calendar,
    Check, X // Added for the checklist rendering
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const PermitManager = ({ initialSelectedId }) => {
    const [activeTab, setActiveTab] = useState('registry'); 
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Ensure we switch to Registry tab if an ID is provided
    useEffect(() => {
        if (initialSelectedId) {
            setActiveTab('registry');
        }
    }, [initialSelectedId]);

    return (
        <div className="animate-fade-in">
            {/* HEADER & TABS */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'25px', paddingBottom:'20px', borderBottom: `1px solid ${THEME.border}`, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '0'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <FileSignature size={28} color={THEME.primary} />
                    <div>
                        <h2 style={{color: 'white', margin:0}}>
                            {activeTab === 'registry' ? 'Permit Control' : 'Configuration'}
                        </h2>
                        <p style={{color: THEME.textSec, margin:'5px 0 0 0', fontSize:'0.9rem'}}>
                            {activeTab === 'registry' ? 'Process approvals and view history.' : 'Design permit templates and workflows.'}
                        </p>
                    </div>
                </div>
                
                <div style={{display:'flex', background: '#0f172a', padding:'4px', borderRadius:'8px', border: `1px solid ${THEME.border}`}}>
                    <button onClick={() => setActiveTab('registry')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'registry' ? THEME.primary : 'transparent', color: activeTab === 'registry' ? 'white' : THEME.textSec, transition: 'all 0.2s', display:'flex', alignItems:'center', gap:'6px'}}><FileSignature size={16}/> Registry</button>
                    <button onClick={() => setActiveTab('config')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'config' ? '#a855f7' : 'transparent', color: activeTab === 'config' ? 'white' : THEME.textSec, transition: 'all 0.2s', display:'flex', alignItems:'center', gap:'6px'}}><Settings size={16}/> Builder</button>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div style={{minHeight:'400px'}}>
                {activeTab === 'registry' ? (
                    <PermitRegistry isMobile={isMobile} initialSelectedId={initialSelectedId} />
                ) : (
                    <PermitBuilder isMobile={isMobile} />
                )}
            </div>
        </div>
    );
};

// ==========================================
// SUB-COMPONENT: REGISTRY (Approvals)
// ==========================================
const PermitRegistry = ({ isMobile, initialSelectedId }) => {
    // If we have an ID, we start in a "loading/determining" state to prevent flashing 'pending' data
    const [subTab, setSubTab] = useState(initialSelectedId ? null : 'pending'); 
    const [permits, setPermits] = useState([]);
    const [roles, setRoles] = useState([]); 
    const [loading, setLoading] = useState(true);
    
    // FILTERS
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [filterDate, setFilterDate] = useState('');

    // Expanded Row State
    const [expandedPermitId, setExpandedPermitId] = useState(null);
    const [reviewComment, setReviewComment] = useState('');

    // 1. LINKING LOGIC: Determine Correct Tab based on ID
    useEffect(() => {
        if (initialSelectedId) {
            setLoading(true);
            api.get(`permits/${initialSelectedId}/`).then(res => {
                const p = res.data;
                // Determine tab based on status
                const targetTab = p.status === 'PENDING' ? 'pending' : 'history';
                setSubTab(targetTab);
                setExpandedPermitId(p.id);
            }).catch(err => {
                console.error("Could not find linked permit", err);
                setSubTab('pending'); // Fallback
            });
        }
    }, [initialSelectedId]);

    // 2. DATA FETCHING: Updated to prevent Race Conditions
    useEffect(() => {
        if (!subTab) return; // Wait until tab is determined

        let isCancelled = false; // FLAG TO PREVENT RACE CONDITIONS
        setLoading(true);

        const fetchData = async () => {
            try {
                let endpoint = 'approvals/todo/';
                if (subTab === 'history') endpoint = 'approvals/history/';
                
                const [pRes, rRes] = await Promise.all([
                    api.get(endpoint),
                    api.get('config/roles-options/') 
                ]);

                // Only update state if this effect is still the "latest" one
                if (!isCancelled) {
                    setPermits(pRes.data);
                    setRoles(rRes.data);
                    setLoading(false);
                }
            } catch (err) { 
                if (!isCancelled) {
                    console.error(err); 
                    setLoading(false);
                }
            } 
        };

        fetchData();

        // Cleanup function: runs when subTab changes or component unmounts
        return () => {
            isCancelled = true;
        };
    }, [subTab]); 

    // 3. SCROLL LOGIC: Runs when permits list loads AND we have an expanded ID
    useEffect(() => {
        if (expandedPermitId && permits.length > 0) {
            const found = permits.find(p => p.id === parseInt(expandedPermitId));
            if (found) {
                setTimeout(() => {
                    const el = document.getElementById(`permit-row-${expandedPermitId}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 500);
            }
        }
    }, [permits, expandedPermitId]);

    // Filter Logic (UPDATED FOR SCOPED IDs)
    const filteredPermits = useMemo(() => {
        return permits.filter(p => {
            const matchSearch = !searchQuery || 
                p.id.toString().includes(searchQuery) ||
                (p.display_id || '').toLowerCase().includes(searchQuery.toLowerCase()) || // Search by Ref Number (#001)
                (p.permit_type_details?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (p.location_label || '').toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchCompany = !filterCompany || 
                (p.submitter_company_name || '').toLowerCase().includes(filterCompany.toLowerCase()) ||
                (p.company_name || '').toLowerCase().includes(filterCompany.toLowerCase());

            const matchDate = !filterDate || (p.date_label || '').includes(filterDate);

            return matchSearch && matchCompany && matchDate;
        });
    }, [permits, searchQuery, filterCompany, filterDate]);

    // Export Logic (UPDATED FOR SCOPED IDs)
    const handleExportList = () => {
        if (filteredPermits.length === 0) return alert("No permits to export");
        const headers = ["ID", "Ref Number", "Type", "Company", "Location", "Date", "Status", "Current Stage", "Description"];
        const csvRows = [headers.join(',')];
        
        filteredPermits.forEach(p => {
            const clean = (t) => `"${(t||'').toString().replace(/"/g, '""')}"`;
            csvRows.push([
                p.id, // Global ID (DB Key)
                clean(p.display_id), // Scoped ID (Visual)
                clean(p.permit_type_details?.name),
                clean(p.company_name),
                clean(p.location_label),
                clean(p.date_label),
                p.status,
                clean(p.status === 'PENDING' && p.next_required_role ? getRoleLabel(p.next_required_role) : '-'),
                clean(p.description)
            ].join(','));
        });

        const url = window.URL.createObjectURL(new Blob([csvRows.join('\n')], { type: 'text/csv' }));
        const link = document.createElement('a'); 
        link.href = url; 
        link.setAttribute('download', `Permits_List_${subTab}_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link); 
        link.click(); 
        link.remove();
    };

    const getRoleLabel = (code) => {
        const found = roles.find(r => r.code === code);
        return found ? found.label : code; 
    };

    // --- UPDATED RENDER FUNCTION TO STRIP EMOJIS ---
    const renderPermitDetails = (text) => {
        if (!text) return <span style={{color:THEME.textSec, fontStyle:'italic'}}>No details provided.</span>;
        
        const lines = text.split('\n');
        
        return lines.map((line, idx) => {
            const separatorIndex = line.lastIndexOf(':');
            
            // Case 1: Checklist Question (Has a colon and isn't a section header)
            if (separatorIndex > -1 && !line.includes('---')) {
                // Clean the question text (remove bullets/emojis)
                const question = line.substring(0, separatorIndex).replace(/•|✅|❌/g, '').trim();
                
                // Clean the answer text
                let answerRaw = line.substring(separatorIndex + 1).trim();
                
                // Detect Logic
                const isYes = answerRaw.toUpperCase().includes('YES') || answerRaw.includes('✅');
                const isNo = answerRaw.toUpperCase().includes('NO') || answerRaw.includes('❌');
                
                // Strip emojis from answer for display
                const answerClean = answerRaw.replace(/✅|❌/g, '').trim();

                return (
                    <div key={idx} style={{display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px dashed ${THEME.border}44`, fontSize:'0.9rem', alignItems:'center'}}>
                        <span style={{color:'#cbd5e1', paddingRight:'15px'}}>{question}</span>
                        
                        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                            {/* Render Icon based on answer */}
                            {isYes && <Check size={16} color={THEME.success} />}
                            {isNo && <X size={16} color={THEME.danger} />}
                            
                            <span style={{
                                color: isYes ? THEME.success : isNo ? THEME.danger : 'white', 
                                fontWeight: (isYes || isNo) ? 'bold' : 'normal'
                            }}>
                                {answerClean}
                            </span>
                        </div>
                    </div>
                );
            } 
            // Case 2: Section Header
            else if (line.includes('---')) {
                return (
                    <div key={idx} style={{
                        color: THEME.primary, 
                        fontWeight:'bold', 
                        marginTop:'20px', 
                        marginBottom:'8px', 
                        fontSize:'0.85rem', 
                        letterSpacing:'0.5px', 
                        textTransform:'uppercase',
                        borderBottom: `1px solid ${THEME.border}`,
                        paddingBottom: '4px'
                    }}>
                        {line.replace(/---|✅|❌/g, '').trim()}
                    </div>
                );
            } 
            // Case 3: Standard Text line
            else {
                if(!line.trim()) return null;
                return <div key={idx} style={{color:THEME.textSec, fontSize:'0.9rem', marginBottom:'4px'}}>{line.replace(/✅|❌/g, '')}</div>;
            }
        });
    };

    const refreshData = async () => {
        if (!subTab) return;
        setLoading(true);
        try {
            let endpoint = 'approvals/todo/';
            if (subTab === 'history') endpoint = 'approvals/history/';
            const [pRes, rRes] = await Promise.all([
                api.get(endpoint),
                api.get('config/roles-options/') 
            ]);
            setPermits(pRes.data);
            setRoles(rRes.data);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleProcess = async (permitId, status) => {
        try {
            await api.post(`approvals/${permitId}/sign/`, { status, comments: reviewComment });
            alert(`Permit ${status === 'APPROVED' ? 'Approved' : 'Rejected'}`);
            setExpandedPermitId(null);
            setReviewComment('');
            refreshData(); // Re-fetch
        } catch (err) { alert("Error processing: " + (err.response?.data?.error || "Unknown")); }
    };

    const handleDownloadPDF = async (permitId) => {
        // Find permit to get display ID for filename
        const p = permits.find(x => x.id === permitId);
        const filename = p ? `Permit_${p.display_id || p.id}.pdf` : `Permit_${permitId}.pdf`;

        try {
            const response = await api.get(`permits/${permitId}/pdf/`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename); // Updated Filename
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) { console.error("PDF Error", err); alert("Error downloading PDF"); }
    };

    const StatusBadge = ({ status }) => {
        let color = THEME.warning;
        let icon = <AlertTriangle size={12} />;
        if (status === 'APPROVED') { color = THEME.success; icon = <CheckCircle2 size={12} />; }
        if (status === 'REJECTED') { color = THEME.danger; icon = <XCircle size={12} />; }
        return <span style={{padding:'4px 10px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold', background:`${color}22`, color:color, border:`1px solid ${color}44`, display:'flex', alignItems:'center', gap:'4px', width:'fit-content'}}>{icon} {status}</span>;
    };

    const inputStyle = { padding:'8px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', fontSize:'0.9rem' };

    return (
        <div>
            {/* SUB-TABS & FILTERS */}
            <div style={{marginBottom:'20px', background:THEME.card, padding:'15px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                <div style={{display:'flex', gap:'10px', marginBottom:'15px', borderBottom:`1px solid ${THEME.border}`, paddingBottom:'15px'}}>
                    {['pending', 'history'].map(tab => (
                        <button key={tab} onClick={() => { setSubTab(tab); setExpandedPermitId(null); }} style={{padding: '6px 12px', borderRadius: '4px', border: `1px solid ${subTab === tab ? THEME.primary : THEME.border}`, background: subTab === tab ? THEME.primary : 'transparent', color: subTab === tab ? 'white' : THEME.textSec, cursor:'pointer', textTransform:'capitalize', flex: isMobile ? 1 : 'none', fontWeight:'bold'}}>{tab}</button>
                    ))}
                </div>
                <div style={{display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center'}}>
                    <div style={{position:'relative', flex: 2, minWidth:'200px'}}>
                        <Search size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                        <input placeholder="Search (ID, Type, Location)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{...inputStyle, width:'100%', boxSizing:'border-box', paddingLeft:'35px'}} />
                    </div>
                    <div style={{position:'relative', flex: 1, minWidth:'120px'}}>
                        <Building2 size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                        <input placeholder="Company..." value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={{...inputStyle, width:'100%', boxSizing:'border-box', paddingLeft:'35px'}} />
                    </div>
                    <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{...inputStyle, width:'auto'}} />
                    <button onClick={handleExportList} style={{padding:'8px 16px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}><Download size={14}/> CSV</button>
                    <button onClick={() => { setSearchQuery(''); setFilterCompany(''); setFilterDate(''); }} style={{padding:'8px 12px', background:'transparent', border:`1px solid ${THEME.textSec}`, color:THEME.textSec, borderRadius:'6px', cursor:'pointer'}}>Clear</button>
                </div>
            </div>

            {/* TABLE */}
            {loading ? <div style={{color:'white', padding:'20px', textAlign:'center'}}>Loading permits...</div> : (
                <div className="card" style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                    <div style={{overflowX: 'auto'}}> 
                        <table style={{width:'100%', minWidth:'700px', borderCollapse:'collapse', textAlign:'left', fontSize:'0.9rem'}}>
                            <thead style={{background:'#020617', borderBottom:`1px solid ${THEME.border}`}}>
                                <tr>
                                    <th style={{padding:'15px', color:THEME.textSec}}>ID</th>
                                    <th style={{padding:'15px', color:THEME.textSec}}>Type</th>
                                    <th style={{padding:'15px', color:THEME.textSec}}>Location</th>
                                    <th style={{padding:'15px', color:THEME.textSec}}>Company</th>
                                    <th style={{padding:'15px', color:THEME.textSec}}>Date</th>
                                    <th style={{padding:'15px', color:THEME.textSec}}>Status</th>
                                    <th style={{padding:'15px', textAlign:'right', color:THEME.textSec}}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPermits.map(p => (
                                    <React.Fragment key={p.id}>
                                        <tr id={`permit-row-${p.id}`} onClick={() => setExpandedPermitId(expandedPermitId === p.id ? null : p.id)} style={{borderBottom:`1px solid ${THEME.border}`, cursor:'pointer', background: expandedPermitId === p.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent'}}>
                                            {/* UPDATED: Show Display ID */}
                                            <td style={{padding:'15px', color:THEME.warning, fontWeight:'bold'}}>{p.display_id || `#${p.id}`}</td>
                                            <td style={{padding:'15px', color:'white', fontWeight:'bold'}}>{p.permit_type_details?.name}</td>
                                            <td style={{padding:'15px', color:'#cbd5e1'}}>{p.location_label}</td>
                                            <td style={{padding:'15px', color:'#cbd5e1'}}>{p.submitter_company_name || p.company_name}</td>
                                            <td style={{padding:'15px', color:'#cbd5e1'}}>{p.date_label}</td>
                                            <td style={{padding:'15px'}}>
                                                <StatusBadge status={p.status} />
                                                {p.status === 'PENDING' && p.next_required_role && <div style={{fontSize:'0.75rem', color:THEME.textSec, marginTop:'6px', background:'rgba(255,255,255,0.05)', padding:'2px 6px', borderRadius:'4px', width:'fit-content', display:'flex', alignItems:'center', gap:'4px'}}><Clock size={10}/> Wait: <strong>{getRoleLabel(p.next_required_role)}</strong></div>}
                                            </td>
                                            <td style={{padding:'15px', textAlign:'right'}}>
                                                <button style={{background:'transparent', border: `1px solid ${THEME.primary}`, color:THEME.primary, padding:'4px 10px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>{expandedPermitId === p.id ? 'Close' : (subTab === 'pending' ? 'Review' : 'View')}</button>
                                            </td>
                                        </tr>
                                        {expandedPermitId === p.id && (
                                            <tr style={{background:'#020617', borderBottom:`2px solid ${THEME.primary}`}}><td colSpan="7" style={{padding:'20px'}}>
                                                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap:'20px'}}>
                                                    <div>
                                                        {/* UPDATED: Show Display ID in Header */}
                                                        <h4 style={{marginTop:0, color:'white', display:'flex', alignItems:'center', gap:'8px'}}><FileSignature size={18}/> Permit Details ({p.display_id || `ID: ${p.id}`})</h4>
                                                        <div style={{background: THEME.card, padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>{renderPermitDetails(p.description)}</div>
                                                        {p.approval_history && p.approval_history.length > 0 && (
                                                            <div style={{marginTop:'20px'}}>
                                                                <h4 style={{color:'white', fontSize:'0.9rem'}}>Approval Chain</h4>
                                                                {p.approval_history.map((sig, i) => (
                                                                    <div key={i} style={{display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px dashed ${THEME.border}`, fontSize:'0.85rem'}}>
                                                                        <div><span style={{color:'white', fontWeight:'bold'}}>{sig.approver_name || getRoleLabel(sig.approver_role)}</span><span style={{color:THEME.textSec, marginLeft:'10px'}}>({getRoleLabel(sig.approver_role)})</span></div>
                                                                        <span style={{color: sig.status==='APPROVED'?THEME.success : THEME.danger, fontWeight:'bold'}}>{sig.status}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{background:THEME.card, padding:'15px', borderRadius:'8px', height:'fit-content', border:`1px solid ${THEME.border}`}}>
                                                        <h4 style={{marginTop:0, color:'white'}}>Actions</h4>
                                                        {subTab === 'pending' ? (
                                                            <>
                                                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Review Comments</label>
                                                                <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} rows="3" style={{width:'100%', padding:'8px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#0f172a', color:'white', boxSizing:'border-box', marginBottom:'15px'}} />
                                                                <div style={{display:'flex', gap:'10px'}}>
                                                                    <button onClick={() => handleProcess(p.id, 'REJECTED')} style={{flex:1, padding:'10px', background:'transparent', border:`1px solid ${THEME.danger}`, color:THEME.danger, borderRadius:'6px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px'}}><XCircle size={16}/> Reject</button>
                                                                    <button onClick={() => handleProcess(p.id, 'APPROVED')} style={{flex:1, padding:'10px', background:THEME.success, border:'none', color:'white', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px'}}><CheckCircle2 size={16}/> Approve</button>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <button onClick={() => handleDownloadPDF(p.id)} style={{width:'100%', padding:'12px', background:THEME.primary, border:'none', color:'white', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}><Download size={16}/> Download PDF</button>
                                                        )}
                                                    </div>
                                                </div>
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {filteredPermits.length === 0 && <tr><td colSpan="7" style={{padding:'40px', textAlign:'center', color:THEME.textSec}}>No permits found in {subTab}.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PermitManager;